import { ComponentStore } from '@ngrx/component-store';
import { MenuItem } from '@mode/shared/ui';
import { ContentTypeMenuItem } from './autocomplete-filters/content-type-menu-item';
import { MatchOnMenuItem } from './autocomplete-filters/match-on-menu-item';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export interface GlobalSearchState {
  filterContentTypeItems: MenuItem[];
  filterMatchOnContentNameItem: FilterItem;
  filterMatchOnCreatorItem: FilterItem;
  filterMatchOnDescriptionItem: FilterItem;
  filterMatchOnQueryItem: FilterItem;
  filterSelectedContentTypeItems: MenuItem[];
}

export interface GlobalSearchViewModel {
  contentTypeItems: MenuItem[];
  matchOnItems: MenuItem[];
  selectedContentTypeItems: MenuItem[];
  selectedMatchOnItems: MenuItem[];
}

export type FilterItem = { menuItem: MenuItem; selected: boolean };

export const defaultGlobalSearchState: GlobalSearchState = {
  filterContentTypeItems: [ContentTypeMenuItem.REPORTS, ContentTypeMenuItem.DATASETS, ContentTypeMenuItem.COLLECTIONS],
  filterMatchOnContentNameItem: { menuItem: MatchOnMenuItem.CONTENT_NAME, selected: true },
  filterMatchOnCreatorItem: { menuItem: MatchOnMenuItem.CREATOR, selected: true },
  filterMatchOnDescriptionItem: { menuItem: MatchOnMenuItem.DESCRIPTION, selected: true },
  filterMatchOnQueryItem: { menuItem: MatchOnMenuItem.QUERY, selected: true },
  filterSelectedContentTypeItems: [
    ContentTypeMenuItem.REPORTS,
    ContentTypeMenuItem.DATASETS,
    ContentTypeMenuItem.COLLECTIONS,
  ],
};

@Injectable({
  providedIn: 'root',
})
export class GlobalSearchStore extends ComponentStore<GlobalSearchState> {
  constructor() {
    super(defaultGlobalSearchState);
  }

  readonly contentTypeItems$ = this.select((state) => state.filterContentTypeItems);
  readonly matchOnContentName$ = this.select((state) => state.filterMatchOnContentNameItem);
  readonly matchOnCreator$ = this.select((state) => state.filterMatchOnCreatorItem);
  readonly matchOnDescription$ = this.select((state) => {
    return {
      ...state.filterMatchOnDescriptionItem,
      menuItem: {
        ...state.filterMatchOnDescriptionItem.menuItem,
        ...(this.isCollectionsOnly(state.filterSelectedContentTypeItems) ? { disabled: true } : {}),
        ...(this.isCollectionsOnly(state.filterSelectedContentTypeItems)
          ? { tooltip: 'This item is not available for your current content selection' }
          : {}),
      },
    };
  });
  readonly matchOnQuery$ = this.select((state) => {
    return {
      ...state.filterMatchOnQueryItem,
      menuItem: {
        ...state.filterMatchOnQueryItem.menuItem,
        ...(this.isCollectionsOnly(state.filterSelectedContentTypeItems) ? { disabled: true } : {}),
        ...(this.isCollectionsOnly(state.filterSelectedContentTypeItems)
          ? { tooltip: 'This item is not available for your current content selection' }
          : {}),
      },
    };
  });

  readonly matchOnItems$ = this.select(
    this.matchOnContentName$,
    this.matchOnCreator$,
    this.matchOnDescription$,
    this.matchOnQuery$,
    (contentName, creator, description, query) => [
      contentName.menuItem,
      creator.menuItem,
      description.menuItem,
      query.menuItem,
    ]
  );
  readonly selectedContentTypeItems$ = this.select((state) => state.filterSelectedContentTypeItems);
  readonly selectedMatchOnItems$ = this.select(
    this.selectedContentTypeItems$,
    this.matchOnContentName$,
    this.matchOnCreator$,
    this.matchOnDescription$,
    this.matchOnQuery$,
    (selectedContentTypeItems, contentName, creator, description, query) => [
      ...(contentName.selected ? [contentName.menuItem] : []),
      ...(creator.selected ? [creator.menuItem] : []),
      ...(description.selected && !this.isCollectionsOnly(selectedContentTypeItems) ? [description.menuItem] : []),
      ...(query.selected && !this.isCollectionsOnly(selectedContentTypeItems) ? [query.menuItem] : []),
    ]
  );

  readonly vm$: Observable<GlobalSearchViewModel> = this.select({
    contentTypeItems: this.contentTypeItems$,
    matchOnItems: this.matchOnItems$,
    selectedContentTypeItems: this.selectedContentTypeItems$,
    selectedMatchOnItems: this.selectedMatchOnItems$,
  });

  private isCollectionsOnly(items: MenuItem[]): boolean {
    return items.length === 1 && items[0] === ContentTypeMenuItem.COLLECTIONS;
  }

  public updateSelectedContentTypeOptions = this.updater((state: GlobalSearchState, items: MenuItem[]) => ({
    ...state,
    filterSelectedContentTypeItems: items,
  }));

  public updateSelectedMatchOnOptions = this.updater((state: GlobalSearchState, items: MenuItem[]) => ({
    ...state,
    filterMatchOnContentNameItem: {
      ...state.filterMatchOnContentNameItem,
      selected: items.some((i) => i.name === MatchOnMenuItem.CONTENT_NAME.name),
    },
    filterMatchOnCreatorItem: {
      ...state.filterMatchOnCreatorItem,
      selected: items.some((i) => i.name === MatchOnMenuItem.CREATOR.name),
    },
    filterMatchOnQueryItem: {
      ...state.filterMatchOnQueryItem,
      selected: items.some((i) => i.name === MatchOnMenuItem.QUERY.name),
    },
    filterMatchOnDescriptionItem: {
      ...state.filterMatchOnDescriptionItem,
      selected: items.some((i) => i.name === MatchOnMenuItem.DESCRIPTION.name),
    },
  }));
}
